import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  cn,
} from '@pantry/ui';
import {
  useSplitGroup,
  type ProductGroupSummary,
  type ProductGroupMemberRow,
} from '@pantry/api-client';
import { ScissorsIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface SplitGroupDialogProps {
  householdId: string;
  group: ProductGroupSummary;
  members: ProductGroupMemberRow[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Dialog for splitting a product group by moving selected members to a new group.
 */
export function SplitGroupDialog({
  householdId,
  group,
  members,
  open,
  onOpenChange,
  onSuccess,
}: SplitGroupDialogProps) {
  const { t } = useLingui();
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const splitGroup = useSplitGroup();

  const toggleProduct = (productId: string) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    setError(null);
    if (!newGroupName.trim()) {
      setError(t`Please enter a name for the new group`);
      return;
    }
    if (selectedProductIds.size === 0) {
      setError(t`Please select at least one product to move`);
      return;
    }
    if (selectedProductIds.size === members.length) {
      toast.error('You must leave at least one product in the original group');
      return;
    }
    setConfirmed(true);
  };

  const handleSplit = async () => {
    try {
      await splitGroup.mutateAsync({
        householdId,
        groupId: group.id,
        newGroupName: newGroupName.trim(),
        productIds: Array.from(selectedProductIds),
      });
      toast.success(t`Group split — "${newGroupName.trim()}" created`);
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error(t`Failed to split group`);
      setConfirmed(false);
    }
  };

  const handleClose = () => {
    setNewGroupName('');
    setSelectedProductIds(new Set());
    setConfirmed(false);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <ScissorsIcon className="h-5 w-5 text-primary" />
            </div>
            <Trans context="dialog_title">Split Group</Trans>
          </DialogTitle>
          <DialogDescription className="pt-1">
            <Trans>Select products to move to a new group.</Trans>
          </DialogDescription>
        </DialogHeader>

        {confirmed ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-muted/50 border border-border p-4 text-sm text-foreground">
              <Trans>
                This will create a new group &ldquo;{newGroupName.trim()}&rdquo; with{' '}
                {selectedProductIds.size} product{selectedProductIds.size !== 1 ? 's' : ''}. The
                source group &ldquo;{group.name}&rdquo; will be modified.
              </Trans>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmed(false)}
                disabled={splitGroup.isPending}
                className="rounded-xl"
              >
                <Trans context="action_button">Back</Trans>
              </Button>
              <Button
                type="button"
                onClick={() => void handleSplit()}
                disabled={splitGroup.isPending}
                className="rounded-xl"
              >
                {splitGroup.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
                    <Trans>Splitting…</Trans>
                  </div>
                ) : (
                  <Trans context="action_button">Confirm Split</Trans>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-group-name">
                <Trans>New group name</Trans>
              </Label>
              <Input
                id="new-group-name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder={t`e.g., Organic Dairy`}
                className="rounded-xl"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label>
                <Trans>Products to move</Trans>
              </Label>
              <div className="rounded-xl border border-border max-h-52 overflow-y-auto divide-y divide-border/50">
                {members.map((member) => (
                  <label
                    key={member.id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors first:rounded-t-xl last:rounded-b-xl',
                      selectedProductIds.has(member.productId) && 'bg-primary/5'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedProductIds.has(member.productId)}
                      onChange={() => toggleProduct(member.productId)}
                      className="rounded border-border accent-primary"
                    />
                    <span className="text-sm text-foreground">
                      {member.productName ?? member.productId}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in">
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={handleClose} className="rounded-xl">
                <Trans context="action_button">Cancel</Trans>
              </Button>
              <Button type="button" onClick={handleConfirm} className="rounded-xl">
                <Trans context="action_button">Review Split</Trans>
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
